import type { Omit } from '@material-ui/core';

import type { ContentItem, Page } from '@swf/cms';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import type {
  ExtractDispatchFromModel,
  ExtractSelectFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import { pageChange } from '@swf/core/utils/actions';

export interface CMSPageState {
  page?: Page;
  content?: Record<string, ContentItem>;
  loading: boolean;
}

const reducers = {
  preloaded: (state: CMSPageState, payload: Omit<CMSPageState, 'loading'>) => ({
    ...state,
    ...payload,
    loading: false
  }),
  setLoading: (state: CMSPageState, payload: boolean) => ({ ...state, loading: payload })
};

const effects = {
  async preload(payload: undefined, params: WidgetEffectParams<void>) {
    const { dispatch, widget, rootState, select } = params;
    const { fetch }: ExtractDispatchFromModel<CMSPageWidgetModel> = dispatch[widget.id];
    pageChange(params.actions)?.subscribe(() => fetch());

    if (!select[widget.id].get(rootState).page) {
      await effects.fetch(undefined, params);
    }
  },
  async fetch(
    payload: undefined,
    { request, dispatch, widget: { id }, select, rootState }: WidgetEffectParams<void>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<CMSPageWidgetModel> = dispatch[id];
    const widgetSelect: ExtractSelectFromModel<CMSPageWidgetModel> = select[id];
    const path = select.location.stripLocale(rootState, undefined);
    widgetDispatch.setLoading(true);

    const { body: data } = await request<Omit<CMSPageState, 'loading'>>({
      service: 'cms.page',
      body: { path }
    });

    if (!data.page && widgetSelect.get(rootState).page?.url === '/404/') {
      widgetDispatch.setLoading(false);
      return;
    }

    widgetDispatch.preloaded(data);
  }
};

const selectors = (slice: Slicer<CMSPageState>) => ({
  get() {
    return slice;
  }
});

export interface CMSPageWidgetModel extends WidgetModelConfig<void, CMSPageState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const cmsPageWidgetModel: CMSPageWidgetModel = {
  state: { loading: false },
  reducers,
  effects,
  selectors
};

export const useCMSPageWidget: UseWidget<void, CMSPageWidgetModel> = useWidget;
